import type { ProjectRecord } from '@/data/useProjects'

export interface PendingReviewItem {
  id: string
  name: string
  /** Stage numbers that have uploaded files but are not yet marked complete. */
  pendingStages: number[]
}

/**
 * Projects with documents "awaiting review": a stage counts when it has at least
 * one uploaded file and is still Not-completed. Files on stages the admin has
 * already resolved — marked Completed (green) or Not-applicable (na) — are not
 * awaiting review. Projects with nothing pending are omitted; the result is
 * sorted by most-pending first.
 */
export function computePendingReview(
  projects: ProjectRecord[],
  stagesWithFiles: Record<string, Set<number>>,
): PendingReviewItem[] {
  const items: PendingReviewItem[] = []

  for (const project of projects) {
    const withFiles = stagesWithFiles[project.id]
    if (!withFiles || withFiles.size === 0) {
      continue
    }

    const pendingStages = [...withFiles]
      .filter((stage) => {
        const status = project.stages?.[String(stage)]?.status
        return status !== 'green' && status !== 'na'
      })
      .sort((a, b) => a - b)

    if (pendingStages.length > 0) {
      items.push({ id: project.id, name: project.name ?? project.id, pendingStages })
    }
  }

  return items.sort((a, b) => b.pendingStages.length - a.pendingStages.length)
}
